'use client'

import React, { useEffect, useState, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { GlowButton } from '@/components/ui/GlowButton'
import type { CarouselApi } from '@/components/ui/carousel'
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import classes from './index.module.css'

export type AdSlide = {
  id?: string
  isActive?: boolean | null
  image: { url?: string | null; alt?: string } | number
  mobileImage?: { url?: string | null; alt?: string } | number | null
  theme?: 'dark' | 'light' | 'brand' | null
  badge?: string | null
  eyebrow?: string | null
  headline?: string | null
  subheadline?: string | null
  ctaText?: string | null
  ctaLink?: string | null
}

interface HomepageAdCarouselProps {
  slides: AdSlide[]
  autoplay?: boolean
  autoplayInterval?: number
}

export const HomepageAdCarousel: React.FC<HomepageAdCarouselProps> = ({
  slides,
  autoplay = true,
  autoplayInterval = 5000,
}) => {
  const activeSlides = slides.filter((s) => s.isActive !== false)
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)
  const [paused, setPaused] = useState(false)
  const prefersReducedMotion = useRef(false)

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  useEffect(() => {
    if (!api) return
    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())
    api.on('select', () => setCurrent(api.selectedScrollSnap()))
  }, [api])

  useEffect(() => {
    if (!api || !autoplay || paused || prefersReducedMotion.current) return
    const timer = setInterval(() => api.scrollNext(), Math.max(2000, autoplayInterval ?? 5000))
    return () => clearInterval(timer)
  }, [api, autoplay, autoplayInterval, paused])

  if (!activeSlides.length) return null

  return (
    <section
      className={classes.adSection}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Promotional carousel"
    >
      <Carousel opts={{ loop: true, align: 'start' }} setApi={setApi} className={classes.carousel}>
        <CarouselContent className={classes.carouselContent}>
          {activeSlides.map((slide, index) => {
            const img = typeof slide.image === 'object' ? slide.image : null
            const mobileImg =
              slide.mobileImage && typeof slide.mobileImage === 'object' ? slide.mobileImage : null
            const hasContent =
              slide.headline || slide.subheadline || slide.ctaText || slide.eyebrow || slide.badge

            return (
              <CarouselItem key={slide.id ?? index} className={classes.carouselItem}>
                <div className={classes.slide}>
                  {img?.url && (
                    <div
                      className={`${classes.imageWrapper}${mobileImg ? ` ${classes.desktopOnly}` : ''}`}
                    >
                      <Image
                        src={img.url}
                        alt={slide.headline ?? img.alt ?? 'Promotional image'}
                        fill
                        priority={index === 0}
                        className={classes.slideImage}
                        sizes="100vw"
                      />
                    </div>
                  )}

                  {mobileImg?.url && (
                    <Image
                      src={mobileImg.url}
                      alt={slide.headline ?? mobileImg.alt ?? 'Promotional image'}
                      fill
                      priority={index === 0}
                      className={`${classes.slideImage} ${classes.mobileOnly}`}
                      sizes="100vw"
                      style={{ objectPosition: 'center' }}
                    />
                  )}

                  <div className={classes.contentOverlay} aria-hidden="true" />
                  <div className={classes.rightVignette} aria-hidden="true" />

                  {hasContent && (
                    <div className={classes.contentWrap}>
                      <div className={classes.contentPanel}>
                        {slide.badge && <span className={classes.badge}>{slide.badge}</span>}
                        {slide.eyebrow && <p className={classes.eyebrow}>{slide.eyebrow}</p>}
                        {slide.headline && (
                          <h2 className={classes.headline}>
                            {slide.headline.split('. ').map((part, i, arr) => {
                              const text =
                                i < arr.length - 1 && !part.endsWith('.') ? `${part}.` : part
                              return (
                                <React.Fragment key={i}>
                                  <span
                                    className={
                                      i === 0 ? classes.headlineStrong : classes.headlineSoft
                                    }
                                  >
                                    {text}
                                  </span>
                                  {i < arr.length - 1 && <br />}
                                </React.Fragment>
                              )
                            })}
                          </h2>
                        )}
                        {slide.subheadline && (
                          <p className={classes.subheadline}>{slide.subheadline}</p>
                        )}
                        {slide.ctaText && slide.ctaLink && (
                          <GlowButton href={slide.ctaLink.trim().replace(/\u00A0/g, '')}>
                            {slide.ctaText}
                          </GlowButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>

        {count > 1 && (
          <>
            <button
              type="button"
              className={`${classes.navBtn} ${classes.navPrev}`}
              onClick={() => api?.scrollPrev()}
              aria-label="Previous slide"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className={`${classes.navBtn} ${classes.navNext}`}
              onClick={() => api?.scrollNext()}
              aria-label="Next slide"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}

        {count > 1 && (
          <div className={classes.dots} role="tablist" aria-label="Carousel slides">
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === current}
                aria-label={`Go to slide ${i + 1}`}
                className={`${classes.dot}${i === current ? ` ${classes.dotActive}` : ''}`}
                onClick={() => api?.scrollTo(i)}
              />
            ))}
          </div>
        )}
      </Carousel>
    </section>
  )
}
